import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, Send, CalendarDays, Sparkles } from "lucide-react";
import { Calendar } from "../ui/calendar";
import { Slider } from "../ui/slider";
import { Reveal, Eyebrow } from "./Reveal";
import { api } from "../../lib/api";
import { BUDGET_TIERS } from "../../data/content";

const inputCls =
  "w-full bg-obsidian/60 border border-woodborder focus:border-burnt text-cream font-body text-sm px-5 py-4 outline-none transition-colors placeholder:text-clay rounded-sm";
const labelCls = "block font-mono text-[10px] tracking-[0.22em] text-tan mb-2";

export const Field = ({ label, error, children, testId }) => (
  <div data-testid={testId}>
    <label className={labelCls}>{label}</label>
    {children}
    {error && <p className="mt-1.5 font-mono text-[11px] text-burnt-hover" data-testid={`${testId}-error`}>{error}</p>}
  </div>
);

export const inputClasses = inputCls;

const fmtDateLocal = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const fmtDisplay = (d) => d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

const DATE_PREFS = [
  { key: "range", label: "PICK MY WINDOW" },
  { key: "flexible", label: "FLEXIBLE ON DATES" },
  { key: "unsure", label: "NOT SURE YET" },
];

export default function GetFeatured() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", website: "", social: "", subject: "", message: "", extra: "" });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const [datePref, setDatePref] = useState("range");
  const [range, setRange] = useState({ from: undefined, to: undefined });
  const [timingNotes, setTimingNotes] = useState("");
  const [tier, setTier] = useState(2);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (form.name.trim().length < 2) errs.name = "Your name is required.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = "A valid email is required.";
    if (!form.subject) errs.subject = "Pick what this is for.";
    if (form.message.trim().length < 5) errs.message = "Tell us the story — at least a sentence.";
    if (datePref === "range" && (!range.from || !range.to)) errs.dates = "Pick a start and end date — or choose flexible / not sure.";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setStatus("loading");
    const tierInfo = BUDGET_TIERS[tier];
    try {
      await api.sendInquiry({
        type: "featured",
        ...form,
        date_preference: datePref === "range" ? "Specific window" : datePref === "flexible" ? "Flexible on dates" : "Not sure yet",
        start_date: datePref === "range" && range.from ? fmtDateLocal(range.from) : null,
        end_date: datePref === "range" && range.to ? fmtDateLocal(range.to) : null,
        timing_notes: timingNotes || null,
        budget_label: tierInfo.display,
        opportunity: tierInfo.message,
      });
      setStatus("success");
    } catch {
      setStatus("error");
    }
  };

  return (
    <section id="get-featured" className="relative py-24 md:py-36 bg-wood border-y border-woodborder" data-testid="get-featured-section">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-5 gap-14">
        <Reveal className="lg:col-span-2">
          <Eyebrow index="10" label="TAP IN" />
          <h2 className="font-display text-cream text-6xl md:text-8xl tracking-tight leading-[0.85]" data-testid="get-featured-heading">
            GET <span className="text-burnt">FEATURED.</span>
          </h2>
          <h3 className="mt-8 font-editorial font-extrabold text-gold-bright text-xl md:text-2xl" data-testid="get-featured-intro-headline">
            Your Story Belongs in the Culture.
          </h3>
          <div className="mt-4 space-y-4 text-sand font-light leading-relaxed max-w-md text-sm md:text-base" data-testid="get-featured-intro">
            <p>
              At The Cobain Report, we're excited to connect with the artists, creatives, entrepreneurs, and brands shaping culture. Every project has a story, and we're interested in discovering the people and ideas behind the work.
            </p>
            <p>
              Whether you're introducing new music, building a brand, launching a product, or sharing your journey, we welcome the opportunity to explore how we can help bring your story to a wider audience.
            </p>
            <p>
              We also believe in transparency. Some features and promotional opportunities involve fees, while others may be explored through custom affiliation or partnership arrangements. Tell us what you're building, when you're looking to make your move, and what level of investment you're considering.
            </p>
            <p className="text-cream font-normal">Let's explore the possibilities together.</p>
          </div>
          <div className="mt-10 space-y-4">
            {["DISCOVER COBAIN", "SUBMIT THE INQUIRY", "THE TEAM RESPONDS"].map((s, i) => (
              <div key={s} className="flex items-center gap-4" data-testid={`get-featured-step-${i}`}>
                <span className="font-display text-2xl text-gold">{`0${i + 1}`}</span>
                <span className="h-px flex-1 bg-woodborder" />
                <span className="font-mono text-[10px] tracking-[0.22em] text-tan">{s}</span>
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.12} className="lg:col-span-3">
          {status === "success" ? (
            <div className="border border-gold/40 bg-card/60 p-10 md:p-14 text-center animate-pulse-glow" data-testid="get-featured-success">
              <span className="mx-auto w-16 h-16 rounded-full bg-gold flex items-center justify-center">
                <Check size={28} className="text-obsidian" />
              </span>
              <h3 className="mt-8 font-display text-4xl md:text-5xl text-cream tracking-wide">INQUIRY RECEIVED.</h3>
              <p className="mt-4 text-sand font-light max-w-sm mx-auto">
                You're on the desk. The Cobain team reviews every inquiry personally — watch your inbox.
              </p>
              <p className="mt-6 font-mono text-[10px] tracking-[0.25em] text-tan">THE COBAIN REPORT — DETROIT</p>
            </div>
          ) : (
            <form onSubmit={submit} className="border border-woodborder bg-card/40 p-7 md:p-10 space-y-6" data-testid="get-featured-form" noValidate>
              <div className="grid sm:grid-cols-2 gap-6">
                <Field label="NAME *" error={errors.name} testId="gf-name">
                  <input className={inputCls} value={form.name} onChange={set("name")} placeholder="Your name" data-testid="gf-name-input" />
                </Field>
                <Field label="EMAIL *" error={errors.email} testId="gf-email">
                  <input className={inputCls} type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" data-testid="gf-email-input" />
                </Field>
                <Field label="PHONE" testId="gf-phone">
                  <input className={inputCls} value={form.phone} onChange={set("phone")} placeholder="(313) ..." data-testid="gf-phone-input" />
                </Field>
                <Field label="COMPANY / BRAND / PROJECT" testId="gf-company">
                  <input className={inputCls} value={form.company} onChange={set("company")} placeholder="What are you building?" data-testid="gf-company-input" />
                </Field>
                <Field label="WEBSITE" testId="gf-website">
                  <input className={inputCls} value={form.website} onChange={set("website")} placeholder="https://" data-testid="gf-website-input" />
                </Field>
                <Field label="SOCIAL MEDIA" testId="gf-social">
                  <input className={inputCls} value={form.social} onChange={set("social")} placeholder="@yourhandle" data-testid="gf-social-input" />
                </Field>
              </div>
              <Field label="WHAT WOULD YOU LIKE TO BE FEATURED FOR? *" error={errors.subject} testId="gf-subject">
                <select className={`${inputCls} appearance-none`} value={form.subject} onChange={set("subject")} data-testid="gf-subject-select">
                  <option value="">Select one</option>
                  {["Interview", "Music Premiere", "Brand / Product Feature", "Event Coverage", "Community Story", "Something Else"].map((o) => (
                    <option key={o} value={o} className="bg-wood">{o}</option>
                  ))}
                </select>
              </Field>
              <Field label="TELL US ABOUT YOURSELF / YOUR STORY *" error={errors.message} testId="gf-story">
                <textarea className={`${inputCls} min-h-[130px] resize-y`} value={form.message} onChange={set("message")} placeholder="The story, the vision, why now..." data-testid="gf-story-textarea" />
              </Field>
              <Field label="ADDITIONAL INFORMATION" testId="gf-extra">
                <textarea className={`${inputCls} min-h-[80px] resize-y`} value={form.extra} onChange={set("extra")} placeholder="Links, press, anything else we should know" data-testid="gf-extra-textarea" />
              </Field>

              {/* ---- Availability calendar ---- */}
              <div className="border-t border-woodborder pt-8" data-testid="gf-availability">
                <div className="flex items-center gap-3 mb-2">
                  <CalendarDays size={16} className="text-burnt" />
                  <h3 className="font-editorial font-extrabold text-cream text-lg md:text-xl">When Are You Looking to Go Live?</h3>
                </div>
                <p className="text-sand text-sm font-light mb-5 max-w-lg">
                  Share your preferred promotional window so our team can better understand your timeline and explore potential availability.
                </p>
                <div className="flex flex-wrap gap-2 mb-5" data-testid="gf-date-prefs">
                  {DATE_PREFS.map((p) => (
                    <button
                      key={p.key}
                      type="button"
                      onClick={() => setDatePref(p.key)}
                      data-testid={`gf-date-pref-${p.key}`}
                      className={`font-mono text-[10px] tracking-[0.18em] px-4 py-2.5 rounded-full border transition-all duration-300 ${
                        datePref === p.key ? "bg-gold border-gold text-obsidian font-bold" : "border-woodborder text-clay hover:border-tan/50 hover:text-tan"
                      }`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
                {datePref === "range" && (
                  <div>
                    <div className="inline-block border border-woodborder bg-obsidian/50 p-3 rounded-sm" data-testid="gf-calendar">
                      <Calendar
                        mode="range"
                        selected={range}
                        onSelect={(r) => setRange(r || { from: undefined, to: undefined })}
                        disabled={{ before: new Date() }}
                        numberOfMonths={1}
                        className="text-cream"
                      />
                    </div>
                    <p className="mt-3 font-mono text-[11px] tracking-[0.15em] text-gold-bright" data-testid="gf-range-display">
                      {range.from ? fmtDisplay(range.from) : "START"} <span className="text-clay">→</span> {range.to ? fmtDisplay(range.to) : "END"}
                    </p>
                    {errors.dates && <p className="mt-1.5 font-mono text-[11px] text-burnt-hover" data-testid="gf-dates-error">{errors.dates}</p>}
                  </div>
                )}
                {datePref !== "range" && (
                  <p className="font-mono text-[11px] tracking-[0.15em] text-tan" data-testid="gf-date-pref-note">
                    {datePref === "flexible" ? "NOTED — YOU'RE FLEXIBLE ON THE WINDOW." : "NOTED — TIMING TBD. WE'LL FIGURE IT OUT TOGETHER."}
                  </p>
                )}
                <div className="mt-5">
                  <label className={labelCls}>TIMING NOTES (OPTIONAL)</label>
                  <input
                    className={inputCls}
                    value={timingNotes}
                    onChange={(e) => setTimingNotes(e.target.value)}
                    placeholder="Release dates, product launches, events, deadlines..."
                    data-testid="gf-timing-notes"
                  />
                </div>
                <p className="mt-3 font-mono text-[9px] tracking-[0.15em] text-clay">THIS SHARES YOUR PREFERRED TIMING — IT DOES NOT RESERVE OR CONFIRM DATES.</p>
              </div>

              {/* ---- Budget glider ---- */}
              <div className="border-t border-woodborder pt-8" data-testid="gf-budget">
                <div className="flex items-center gap-3 mb-2">
                  <Sparkles size={16} className="text-gold" />
                  <h3 className="font-editorial font-extrabold text-cream text-lg md:text-xl">What Are You Looking to Invest in Your Exposure?</h3>
                </div>
                <p className="text-sand text-sm font-light mb-7 max-w-lg">
                  Every opportunity is different. Explore the level of investment you're considering to discover potential ways to feature your story, promote your work, or connect your brand with The Cobain Report.
                </p>
                <div className="px-1">
                  <Slider
                    min={0}
                    max={BUDGET_TIERS.length - 1}
                    step={1}
                    value={[tier]}
                    onValueChange={([v]) => setTier(v)}
                    data-testid="gf-budget-slider"
                    aria-label="Budget range"
                  />
                  <div className="flex justify-between mt-2 font-mono text-[9px] tracking-[0.15em] text-clay">
                    <span>UNDER $50</span>
                    <span>$1,000+</span>
                  </div>
                </div>
                <div className="mt-6 border border-gold/30 bg-gold/5 p-6" data-testid="gf-budget-display">
                  <p className="font-display text-4xl md:text-5xl text-gold-bright tracking-wide" data-testid="gf-budget-value">
                    {BUDGET_TIERS[tier].display.toUpperCase()}
                  </p>
                  <AnimatePresence mode="wait">
                    <motion.p
                      key={tier}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      transition={{ duration: 0.25 }}
                      className="mt-3 text-sand text-sm font-light"
                      data-testid="gf-budget-message"
                    >
                      {BUDGET_TIERS[tier].message}
                    </motion.p>
                  </AnimatePresence>
                </div>
                <p className="mt-4 font-mono text-[9px] tracking-[0.15em] text-clay leading-relaxed" data-testid="gf-budget-disclosure">
                  YOUR SELECTED BUDGET HELPS US UNDERSTAND YOUR GOALS AND EXPLORE SUITABLE OPPORTUNITIES. FINAL PRICING, SCOPE, AND AVAILABILITY WILL BE DISCUSSED AND CONFIRMED SEPARATELY.
                </p>
              </div>

              {status === "error" && (
                <p className="font-mono text-[11px] text-burnt-hover" data-testid="gf-form-error">Submission glitched. Give it another shot.</p>
              )}
              <button
                type="submit"
                disabled={status === "loading"}
                data-testid="gf-submit"
                className="w-full flex items-center justify-center gap-3 bg-burnt hover:bg-burnt-hover text-cream font-mono text-xs tracking-[0.25em] px-8 py-5 rounded-full transition-all duration-300 hover:shadow-[0_0_36px_rgba(200,90,23,0.45)] disabled:opacity-60"
              >
                <Send size={14} />
                {status === "loading" ? "SENDING..." : "SUBMIT INQUIRY"}
              </button>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
}
